import type { ReactNode } from 'react';
import React, { Children, useState } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { useSpring, animated, config } from '@react-spring/web';
import { ICONS } from 'Components/utils/icon/icons';
import { useMeasure } from 'Components/utils/measure/measure';
import ratios from 'Components/utils/ratios/ratios.utils';
import { t } from 'Src/i18n';
import { Badge } from 'Components/frameworks/badge/Badge';
import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink';
import { Icon } from 'Components/frameworks/icon/Icon';
import { Image } from 'Components/frameworks/image/Image';
import { TemplateString } from 'Components/frameworks/template-string/TemplateString';
import { Text } from 'Components/frameworks/text/Text';
import { Title } from 'Components/frameworks/title/Title';
const MIN_HEIGHT = 60;
type Props = {
  children?: ReactNode;
  className?: string;
  imageUrl: string;
  isChecked?: boolean;
  isDisabled?: boolean;
  label: string;
  onClick?: () => void;
  price?: string | null | undefined;
  text?: string;
  url?: string;
};
export const UpsellCard = ({
  children,
  className,
  imageUrl,
  isChecked = false,
  isDisabled = false,
  label,
  onClick,
  price,
  text,
  url,
  ...rest
}: Props) => {
  const [isOpened, setIsOpened] = useState<boolean>(false);
  const [isFinish, setIsFinish] = useState<boolean>(true);
  const [bind, bounds] = useMeasure();
  const measuredHeight = 'height' in bounds ? bounds.height : MIN_HEIGHT;
  const styles = useSpring({
    config: config.stiff,
    from: {
      height: MIN_HEIGHT,
    },
    to: {
      height: isOpened ? measuredHeight : MIN_HEIGHT,
    },
    onStart: () => setIsFinish(false),
    onRest: ({ value }) => setIsFinish(value.height === MIN_HEIGHT),
  });
  const hasExpand = Boolean(Children.count(children));
  const TagName = url ? DynamicLink : 'button';
  const props = url
    ? {
        shallow: true,
        url,
      }
    : {
        type: 'button',
      };
  return (
    <article
      {...rest}
      className={classnames(
        'text-initial relative isolate flex min-h-140 items-start gap-15 rounded-12 border-1 border-solid border-lightGrey bg-white p-10 shadow',
        className,
      )}
    >
      {isChecked && (
        <span className="absolute end-0 top-0 m-5">
          <Badge
            className="size-20 border-green bg-green text-white"
            iconName={ICONS.CHECK}
            variant="pill"
          />
        </span>
      )}

      <div className="my-10 aspect-square size-100 shrink-0 overflow-hidden rounded-full bg-grey">
        <Image imageUrl={imageUrl} ratioDisplay={1 / 4} ratioKey={ratios.SQUARE} />
      </div>

      <div className="flex-1">
        <Title className="mb-2" theme="title-sans-n3">
          {label}
        </Title>
        <animated.div
          style={{
            overflow: 'hidden',
            ...styles,
          }}
        >
          <div {...bind}>
            <Text
              className={classnames('text-14', {
                'line-clamp-3': isFinish,
              })}
              text={text}
            />
            {children}
          </div>
        </animated.div>
        <div className="mt-10 flex items-center justify-between gap-10 border-t-1 border-lightGrey pt-5">
          {price && (
            <p className="text-12">
              <TemplateString
                template={t('common.price.priceFrom')}
                valuesClassName={{
                  price: 'font-bold text-16 text-sienna',
                }}
                values={{
                  currency: '',
                  price: `+ ${price}`,
                }}
              />
            </p>
          )}
          {!isDisabled && (
            <TagName
              {...(props as React.ComponentProps<typeof DynamicLink>)}
              className="ms-auto flex gap-5 text-14 text-black underline"
              onClick={() => {
                hasExpand && !url && setIsOpened((v) => !v);
                onClick?.();
              }}
            >
              {t('common.viewMore')}
              {hasExpand && (
                <Icon
                  className="mt-5"
                  name={ICONS.ARROW_DOWN}
                  rotation={isOpened ? 180 : 0}
                  width="1em"
                />
              )}
            </TagName>
          )}
        </div>
      </div>
    </article>
  );
};
